import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vShow as _vShow, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "cuscd-item" }
const _hoisted_2 = {
  key: 0,
  class: "cuscd-item-inner cuscd-rounded loading-item"
}
const _hoisted_3 = {
  key: 1,
  class: "cuscd-item-inner cuscd-rounded documents-tile"
}
const _hoisted_4 = { class: "documents-tile-content" }
const _hoisted_5 = { class: "cuscd-h2style cuscd-item-header" }
const _hoisted_6 = { class: "documents-section" }
const _hoisted_7 = {
  key: 0,
  class: "table-wrapper"
}
const _hoisted_8 = { class: "table generatedDocuments" }
const _hoisted_9 = { class: "createdAtWrapper" }
const _hoisted_10 = ["onClick", "disabled"]
const _hoisted_11 = { class: "open-document-in-new-tab" }
const _hoisted_12 = ["onClick", "disabled"]
const _hoisted_13 = {
  key: 0,
  class: "disclaimer"
}
const _hoisted_14 = ["href"]
const _hoisted_15 = {
  key: 1,
  class: "no-documents"
}
const _hoisted_16 = { class: "documents-section" }
const _hoisted_17 = {
  key: 0,
  class: "table-wrapper"
}
const _hoisted_18 = { class: "table" }
const _hoisted_19 = {
  key: 0,
  class: "pagination"
}
const _hoisted_20 = {
  key: 1,
  class: "no-documents"
}

import { ref, reactive, computed, onMounted, nextTick, watch } from "vue";
import { useStore } from "vuex";
import Loader from "@/components/shared/Loader.vue";
import Page from "@/components/shared/layout/Page.vue";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import useFormatter from "@/use/formatter";
import useFileUtilities from "@/use/fileUtilities";
import { FieldSchema, FormField, Option } from "@/models/form";
import useApplications from "@/use/application";
import Error from "@/components/shared/Error.vue";
import { $api } from "@/services/api";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InfoTooltip from "@/components/shared/tooltips/InfoTooltip.vue";
import { DocumentNameConstants } from "@/config/constants";
import { DocumentStatuses } from "@/config/enums";


export default /*@__PURE__*/_defineComponent({
  __name: 'Documents',
  setup(__props) {

const store = useStore();
const { base64ToBlob } = useFileUtilities();

// Reactive state
const loading = ref<boolean>(true);
const downloading = ref<boolean>(false);
const retrievingDocument = ref<boolean>(false);

let documents = reactive([]) as any[];
let uploadedDocuments = reactive([]) as any[];
let generatedDocuments = reactive([]) as any[];
const { formatDateTime, formatBytes } = useFormatter();
const buttonTypes = computed(() => {
  let types = ["uploaded", "generated"];
  return types;
});
const documentsState = ref({ selected: "uploaded" as string, documents: [] as any[] });
const linkRefs = reactive({} as any);
const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);
const oidcIsAuthenticated = computed(() => store.getters["oidcStore/oidcIsAuthenticated"]);
const tollFreeNumberHref = computed<string>(() => store.getters["lenderTollFreeNumberHref"]);
const tollFreeNumber = computed<string>(() => store.getters["lenderTollFreeNumber"]);
const applicationId = computed<number>(() => store.state.appData?.application?.id);

// Methods
async function loadDocuments() {
  window.scrollTo(0, 0);

  // authenticated user only
  if (oidcUser.value && oidcIsAuthenticated.value) {
    if (
      store.state.appData.applicantDocuments === undefined ||
      store.state.appData.applicantDocuments.length === 0 ||
      store.state.appData.generatedDocuments === undefined ||
      store.state.appData.generatedDocuments.length === 0
    ) {
      await store.dispatch("rehydrateStore");
    }
    if (store.state.appData.applicantDocuments.length > 0) {
      uploadedDocuments = store.state.appData.applicantDocuments
        .filter(
          (d: any) =>
            [Number(DocumentStatuses.Signed), Number(DocumentStatuses.Generated)].includes(
              d.documentStatusId,
            ) === false,
        )
        .map((doc: any) => {
          return {
            name: doc?.uploadedDocumentMetadata?.originalFileName
              ? doc?.uploadedDocumentMetadata?.originalFileName
              : "Not Available",
            size: doc?.uploadedDocumentMetadata?.fileLength
              ? formatBytes(doc?.uploadedDocumentMetadata?.fileLength)
              : "Not Available",
            createdAt: doc?.createdAt ? formatDateTime(doc?.createdAt) : "Not Available",
            documentId: doc?.documentId,
            documentType: doc?.documentType,
          };
        });
    }
    if (store.state.appData.generatedDocuments.length > 0) {
      generatedDocuments = store.state.appData.generatedDocuments;
    }
  }

  loading.value = false;
}
async function previous() {
  if (pagination.currentPage > 1) {
    pagination.currentPage--;
    loading.value = true;
    await loadDocuments();
  }
}
async function next() {
  if (pagination.currentPage < pagination.totalPages) {
    window.scrollTo(0, 0);
    pagination.currentPage++;
    loading.value = true;
    await loadDocuments();
  }
}
async function changePage(newPage: string) {
  window.scrollTo(0, 0);
  pagination.currentPage = parseInt(newPage);
  loading.value = true;
  await loadDocuments();
}
async function openDocumentInNewTab(document: any) {
  retrievingDocument.value = true;
  document.showViewDocumentDisclaimerText = false;

  // Open window synchronously before await — required for iOS Safari popup policy
  const newWindow = window.open("", "_blank");

  let documentViewLink = "";
  if (document?.documentName === DocumentNameConstants.DESIGNATION_OF_AUTHORIZED_INDIVIDUAL_FORM) {
    documentViewLink = document?.cdnDocumentUrl;
  } else {
    documentViewLink = await $api.documents.generateDocumentViewLink({
      applicantId: document.applicantId,
      applicationId: applicationId.value,
      documentName: document.documentName,
      documentId: document.documentId,
    });
  }

  if (documentViewLink && newWindow) {
    newWindow.location.href = documentViewLink;
  } else {
    newWindow?.close();
  }
  document.showViewDocumentDisclaimerText = true;
  retrievingDocument.value = false;
}
async function getDownloadLink(document: any) {
  if (document?.documentName === DocumentNameConstants.DESIGNATION_OF_AUTHORIZED_INDIVIDUAL_FORM) {
    return document?.cdnDocumentUrl;
  } else {
    return await $api.documents.downloadDocument({ documentId: document.documentId });
  }
}
async function download(document: any) {
  downloading.value = true;
  nextTick(async () => {
    let refId = getRefId(document);
    let link = linkRefs[refId] as HTMLAnchorElement;
    let downloadLink = await getDownloadLink(document);

    if (downloadLink) {
      let a = link as HTMLAnchorElement;
      a.setAttribute("download", `${document?.documentName?.replace(/ /g, "")}`);
      a.setAttribute("href", `${downloadLink}`);
      a.setAttribute("target", "_blank");
      a.click();
      a.removeAttribute("href");
      a.removeAttribute("download");
      a.removeAttribute("target");
    } else {
      console.log("FAILED TO DOWNLOAD");
    }

    downloading.value = false;
  });
}
function showDocumentNameTooltip(document: any) {
  return document?.documentName === DocumentNameConstants.DESIGNATION_OF_AUTHORIZED_INDIVIDUAL_FORM;
}
function documentNameTooltipText(document: any) {
  if (document?.documentName === DocumentNameConstants.DESIGNATION_OF_AUTHORIZED_INDIVIDUAL_FORM) {
    return "Optional form for you to complete if you want to designate someone to act on your behalf in the event of your death";
  } else {
    return "";
  }
}
function getRefId(document: any) {
  return `download${document.documentId}${document?.documentName?.replace(/ /g, "")}`;
}
function handleButtonClick(type: string) {
  documentsState.value.selected = type;
  documentsState.value.documents = type === "uploaded" ? uploadedDocuments : generatedDocuments;
}
function getIcon(type: string) {
  return type === "uploaded" ? "file-upload" : "file-contract";
}
function getTitle(type: string) {
  return type === "uploaded" ? "Uploaded Documents" : "Generated Documents";
}

// Table Reactive State
const tableFieldSchema = reactive({
  type: "select",
  required: false,
  hideLabel: true,
  validate: (schema: FormField) => (schema.valid = true),
} as FieldSchema);

// TOTAL PAGES
const pagination = reactive({
  currentPage: 1,
  perPage: 10,
  totalPages: computed(() => Math.ceil(documents.length / pagination.perPage)),
  pages: [],
}) as any;

const paginatedItems = computed(() => {
  const { currentPage, perPage } = pagination;

  if (!currentPage) {
    return uploadedDocuments;
  } else {
    const start = (currentPage - 1) * perPage;
    const stop = start + perPage;

    return uploadedDocuments.slice(start, stop);
  }
});

const pages = computed<Array<any>>((i: any) => {
  return [...Array(pagination.totalPages).keys()].map(i => i + 1);
});

// Sort functionality
const sort = reactive({ column: "", direction: "asc" });
function sortDocuments(column: string) {
  sort.direction = sort.column === column && sort.direction !== "asc" ? "asc" : "desc";
  sort.column = column;

  paginatedItems.value.sort((a: any, b: any) => {
    let modifier = 1;
    if (sort.direction === "desc") {
      modifier = -1;
    }
    if (a[sort.column] < b[sort.column]) {
      return -1 * modifier;
    }
    if (a[sort.column] > b[sort.column]) {
      return 1 * modifier;
    } else {
      return 0;
    }
  });
}

function sortClasses(column: string) {
  return {
    "sort-asc": sort.column === column && sort.direction === "asc",
    "sort-desc": sort.column === column && sort.direction === "desc",
  };
}

onMounted(() => {
  loadDocuments();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Page, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(Loader)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("span", null, [
                  _createElementVNode("h1", _hoisted_5, [
                    _cache[5] || (_cache[5] = _createTextVNode(" My Documents ", -1)),
                    _createVNode(InfoTooltip, null, {
                      tooltipText: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                        _createElementVNode("p", null, " When opening Application Documents, please be sure your pop-up blocker is turned off. You can also download the documents by selecting the download button. Uploaded Documents are not viewable in your portal. ", -1)
                      ]))]),
                      _: 1
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _cache[9] || (_cache[9] = _createElementVNode("p", { class: "cuscd-h2style cuscd-item-header" }, "Application Documents", -1)),
                  (_unref(generatedDocuments)?.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                        _createElementVNode("table", _hoisted_8, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(generatedDocuments), (document) => {
                            return (_openBlock(), _createElementBlock("tr", {
                              key: document.documentId
                            }, [
                              _createElementVNode("td", {
                                class: _normalizeClass([!document?.documentTypeName ? 'bold' : ''])
                              }, [
                                _createTextVNode(_toDisplayString(document?.documentTypeName
                        ? document?.documentTypeName
                        : document?.documentName) + " ", 1),
                                _withDirectives(_createVNode(InfoTooltip, null, {
                                  tooltipText: _withCtx(() => [
                                    _createElementVNode("p", null, _toDisplayString(documentNameTooltipText(document)), 1)
                                  ]),
                                  _: 2
                                }, 1536), [
                                  [_vShow, showDocumentNameTooltip(document)]
                                ])
                              ], 2),
                              _createElementVNode("td", null, [
                                _createElementVNode("div", _hoisted_9, [
                                  _createElementVNode("span", null, _toDisplayString(document?.createdAt), 1),
                                  _createElementVNode("span", null, [
                                    (document.isDownloadable)
                                      ? (_openBlock(), _createElementBlock("a", {
                                          key: 0,
                                          class: "download",
                                          onClick: ($event: any) => (download(document)),
                                          disabled: downloading.value
                                        }, [
                                          _createVNode(_unref(FontAwesomeIcon), {
                                            class: "download-icon",
                                            icon: ['fas', 'cloud-arrow-down']
                                          }),
                                          _cache[6] || (_cache[6] = _createTextVNode(" ", -1))
                                        ], 8, _hoisted_10))
                                      : _createCommentVNode("", true),
                                    _createElementVNode("a", {
                                      ref_for: true,
                                      ref: 
                            el => {
                              linkRefs[getRefId(document)] = el;
                            }
                          
                                    }, null, 512)
                                  ]),
                                  _createElementVNode("span", null, [
                                    _createElementVNode("div", _hoisted_11, [
                                      _createElementVNode("a", {
                                        class: "link",
                                        onClick: ($event: any) => (openDocumentInNewTab(document)),
                                        disabled: retrievingDocument.value
                                      }, "Open Document in New Tab", 8, _hoisted_12),
                                      (document?.showViewDocumentDisclaimerText)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_13, [
                                            _cache[7] || (_cache[7] = _createTextVNode(" Not seeing your document? Make sure you turn off pop-up blockers and use the most recent version of your browser. If you’re still having issues, please call us at ", -1)),
                                            _createElementVNode("a", {
                                              class: "phone",
                                              href: `tel:+${tollFreeNumberHref.value}`
                                            }, _toDisplayString(tollFreeNumber.value), 9, _hoisted_14),
                                            _cache[8] || (_cache[8] = _createTextVNode(". ", -1))
                                          ]))
                                        : _createCommentVNode("", true)
                                    ])
                                  ])
                                ])
                              ])
                            ]))
                          }), 128))
                        ])
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_15, " There Are No Application Documents Associated With This Application "))
                ]),
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("div", null, [
                    _cache[11] || (_cache[11] = _createElementVNode("p", { class: "cuscd-h2style cuscd-item-header" }, "Uploaded Documents", -1)),
                    (paginatedItems.value?.length > 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                          _createElementVNode("table", _hoisted_18, [
                            _createElementVNode("thead", null, [
                              _createElementVNode("tr", null, [
                                _createElementVNode("th", {
                                  style: {"min-width":"40%"},
                                  scope: "col",
                                  onClick: _cache[0] || (_cache[0] = ($event: any) => (sortDocuments('name'))),
                                  class: _normalizeClass(sortClasses('name'))
                                }, " Name ", 2),
                                _createElementVNode("th", {
                                  scope: "col",
                                  onClick: _cache[1] || (_cache[1] = ($event: any) => (sortDocuments('size'))),
                                  class: _normalizeClass(sortClasses('size'))
                                }, " Size ", 2),
                                _createElementVNode("th", {
                                  scope: "col",
                                  onClick: _cache[2] || (_cache[2] = ($event: any) => (sortDocuments('createdAt'))),
                                  class: _normalizeClass(sortClasses('createdAt'))
                                }, " Created On ", 2)
                              ])
                            ]),
                            _createElementVNode("tbody", null, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedItems.value, (document) => {
                                return (_openBlock(), _createElementBlock("tr", {
                                  key: document.id
                                }, [
                                  _createElementVNode("td", null, _toDisplayString(document?.name), 1),
                                  _createElementVNode("td", null, _toDisplayString(document?.size), 1),
                                  _createElementVNode("td", null, _toDisplayString(document?.createdAt || document.fileSize), 1)
                                ]))
                              }), 128))
                            ])
                          ]),
                          (pages.value.length > 1)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                _cache[10] || (_cache[10] = _createElementVNode("p", null, "Page", -1)),
                                _createVNode(BaseFormField, {
                                  modelValue: pagination.currentPage,
                                  "onUpdate:modelValue": [
                                    _cache[3] || (_cache[3] = ($event: any) => ((pagination.currentPage) = $event)),
                                    changePage
                                  ],
                                  schema: tableFieldSchema,
                                  options: pages.value
                                }, null, 8, ["modelValue", "schema", "options"]),
                                _createElementVNode("p", null, "of " + _toDisplayString(pagination.totalPages), 1),
                                _withDirectives(_createElementVNode("button", {
                                  type: "button",
                                  class: "btn btn-primary",
                                  onClick: previous
                                }, " Previous ", 512), [
                                  [_vShow, pagination.currentPage > 1]
                                ]),
                                _withDirectives(_createElementVNode("button", {
                                  type: "button",
                                  class: "btn btn-primary",
                                  onClick: next
                                }, " Next ", 512), [
                                  [_vShow, pagination.currentPage < pagination.totalPages]
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_20, " There Are No Uploaded Documents Associated With This Application "))
                  ])
                ])
              ])
            ]))
      ])
    ]),
    _: 1
  }))
}
}

})